import type { FunctionComponent, ReactNode } from 'react';

import { withTargetChecked } from '../../../utils';
import { LabeledCheckbox } from '../Forms';
import { icons } from '../Icon';
import TooltipIcon from '../TooltipIcon';

export type FilterOption = {
  key: string;
  label: string;
  tooltip?: string;
};

export type SelectedOptionsProps = {
  /**
   * Array of selected option keys
   */
  selectedOptions?: string[];
  /**
   * Callback function to be called when a filter option is selected/unselected.
   */
  onSelect: (key: string, isSelected: boolean) => void;
};

export type CheckboxFilterGroupProps = SelectedOptionsProps & {
  /**
   * Filter group title
   */
  title: string;
  /**
   * Array of filter options
   */
  options: FilterOption[];
  /**
   * parent test id to be added to the group. Each checkbox will get the test id of `${testId}-${key}`
   */
  testId?: string;
};

const selectedOptionsDefault: string[] = [];

/**
 * Helper for creating checkbox filter labels with optional tooltips.
 */
const createCheckboxLabel = (option: FilterOption): ReactNode => {
  return (
    <>
      <span>{option.label}</span>
      {option.tooltip && (
        <span className="checkbox-filter-group-tooltip">
          <TooltipIcon
            id={`tooltip-${option.key}`}
            icon={icons.questionCircle}
            tooltip={option.tooltip}
          />
        </span>
      )}
    </>
  );
};

/**
 * Renders a filter group with filter options as checkboxes
 * ALEX TODO: VoidFunctionComponent doesn't get parsed properly
 * @deprecated - Use FilterCardCheckboxes instead
 */
const CheckboxFilterGroup: FunctionComponent<CheckboxFilterGroupProps> = ({
  title,
  options,
  selectedOptions = selectedOptionsDefault,
  onSelect,
  testId,
}) => {
  const onChange = withTargetChecked((isChecked, event) => {
    onSelect(event.target.id, isChecked);
  });

  return (
    <div className="checkbox-filter-group" data-test-id={testId}>
      <span className="checkbox-filter-group-title">{title}</span>
      <div className="checkbox-filter-group-checks">
        {options.map(option =>
          <LabeledCheckbox
            key={option.key}
            id={option.key}
            isChecked={selectedOptions?.includes(option.key)}
            label={createCheckboxLabel(option)}
            onChange={onChange}
            testId={testId ? `${testId}--${option.key}` : undefined}
            isRippleDisabled
          />)}
      </div>
    </div>
  );
};

export default CheckboxFilterGroup;
